if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        // TODO 6: Base-Order + ID→Element-Maps für Farb-Reorder (siehe gallery-reorder.js).
        const baseMains = [...this.elements.mains];
        const baseThumbs = [...this.querySelectorAll(".product-media-thumbnail")];
        this.mainById = new Map(baseMains.map((el) => [el.dataset.mediaId, el]));
        this.thumbById = new Map(baseThumbs.map((el) => [el.dataset.mediaId, el]));
        this.baseMediaItems = baseMains.map((el) => ({
          id: el.dataset.mediaId,
          color: el.dataset.mediaColor || "",
        }));
        this.thumbsWrapper = this.querySelector(".product-media-thumbnails");

        if (!this.elements.thumbnails.length) {
          return;
        }

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }
          mediaToggle.addEventListener(
            "click",
            this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
          );
        });
      }

      connectedCallback() {
        this.subscribeVariantChange();

        // Initial-Filter: Galerie beim Laden auf die vorgewählte Farbe einschränken
        // (deferred, damit Flickity ggf. zuerst initialisiert).
        setTimeout(() => this.applyColorView(), 0);

        if (!window.Flickity) {
          return;
        }

        this.elements.slideshow.style.display = "block";
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: ".product-media",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: true,
          watchCSS: true,
          on: {
            change: (index) => {
              const { element } = this.flickity.cells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId);
            },
          },
        });
      }

      setActiveMedia(mediaId, prepend, suppressScroll) {
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = [...this.elements.mains].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });
        // Beim Farb-Filter kann das Ziel-/aktive Medium aus dem DOM gelöst sein.
        if (!nextMainMedia) {
          return;
        }
        const index = [...this.elements.mains].indexOf(nextMainMedia);

        if (this.flickity) {
          this.flickity.select(index);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
        });
        const thumbnail = [...this.elements.thumbnails].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });

        activeMainMedia?.classList.remove("is-active");

        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        this.playActiveMedia(nextMainMedia);

        if (!thumbnail) {
          // The code below is for thumbnails, return early
          // if there are not visible (e.g. in the quick view drawer).
          return;
        }

        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else if (!suppressScroll && window.matchMedia("(min-width: 750px)").matches) {
          // Nur Desktop: aktives Thumbnail in den Strip scrollen. Auf Mobile
          // liegt der Thumbnail-Strip ganz oben → scrollIntoView riss die Seite
          // bei JEDEM Varianten-Wechsel (Farbe/Größe) vom Picker nach oben
          // (Maranello-scripts.js#updateMedia ruft setActiveMedia bei jedem
          // product:variant-change). David, 2026-06-15.
          // suppressScroll: der Farb-Filter (applyOrder) ruft setActiveMedia
          // programmatisch auf. scrollIntoView(block:"nearest") scrollt dann auch
          // das FENSTER hoch zur (nach oben gerückten) Galerie → Seite sprang beim
          // Farbwechsel vom Picker nach oben. Reine Thumbnail-Klicks scrollen
          // weiterhin. David, 2026-07-01.
          thumbnail.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector("deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      subscribeVariantChange() {
        if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
          return;
        }
        this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.productVariantChange, () => {
          // Tick warten, damit der Picker seine checked-States aktualisiert hat.
          setTimeout(() => this.applyColorView(), 0);
        });
      }

      getSelectedColor() {
        const section = this.closest(".shopify-section") || document;
        const picker = section.querySelector("variant-radios, variant-selects");
        if (!picker) {
          return "";
        }
        // Rolle + Wert-Schlüssel statt Anzeigetext: auf lokalisierten Storefronts
        // heißt die Option "Color" und ihr Wert "Black". Die Bild-Dateinamen bleiben
        // deutsch, also vergleichen wir gegen den stabilen Schlüssel (`schwarz`).
        // data-option-name/value bleibt als Fallback für ungepatchte Templates.
        const checked =
          picker.querySelector('fieldset[data-option-role="color"] input:checked') ||
          picker.querySelector('fieldset[data-option-name="Farbe"] input:checked');
        if (checked) {
          return checked.dataset.optionValueKey || checked.value;
        }
        const select =
          picker.querySelector('select[data-option-role="color"]') ||
          picker.querySelector('select[name="options[Farbe]"]');
        if (!select) {
          return "";
        }
        const selected = select.selectedOptions && select.selectedOptions[0];
        return (selected && selected.dataset.optionValueKey) || select.value;
      }

      applyColorView() {
        const reorder = window.CECGalleryReorder;
        if (
          !reorder ||
          typeof reorder.visibleByColor !== "function" ||
          !this.baseMediaItems ||
          !this.baseMediaItems.length
        ) {
          return;
        }
        const color = this.getSelectedColor();
        const want = reorder.normalizeColor(color);
        // Farbe unverändert (z.B. nur Größe/Zustand gewechselt) → Galerie nicht stören.
        if (want === this._lastAppliedColor) {
          return;
        }
        this._lastAppliedColor = want;
        const view = reorder.visibleByColor(this.baseMediaItems, color);
        if (!view.filtered) {
          // Nichts zu filtern (keine Farb-Bilder o.ä.). Nur falls vorher gefiltert
          // war, alle Medien wieder einblenden; sonst native Anzeige unangetastet.
          if (this._wasFiltered) {
            this._wasFiltered = false;
            this.applyOrder(view.ids);
          }
          return;
        }
        this._wasFiltered = true;
        this.applyOrder(view.ids);
      }

      applyOrder(idOrder) {
        if (!idOrder || !idOrder.length) {
          return;
        }
        // Farb-Filter: Medien, die NICHT in idOrder stehen, komplett aus dem DOM
        // lösen. display:none reicht für Flickity nicht — entfernte Zellen
        // verschwinden erst nach reloadCells, wenn sie nicht mehr im DOM sind.
        const showSet = new Set(idOrder);
        // Haupt-Medien: nur über baseMediaItems (bei hide_variants=true existieren
        // Variant-Mains ohnehin nicht).
        this.baseMediaItems.forEach((m) => {
          if (!showSet.has(m.id)) {
            const mEl = this.mainById.get(m.id);
            if (mEl && mEl.parentNode) mEl.parentNode.removeChild(mEl);
          }
        });
        // Thumbnails: ÜBER ALLE (thumbById), inkl. .product-media-thumbnail-variant.
        // Diese Variant-Thumbnails haben bei hide_variants=true KEIN zugehöriges
        // .product-media-main → sie stehen nicht in baseMediaItems und würden von der
        // obigen Schleife nie entfernt. Zurückgelassen rutschen sie nach dem Reorder
        // auf :first-child (CSS-Hide greift nicht mehr → z.B. schwarzes Bild sichtbar)
        // und bleiben ohne Klick-Handler zurück. Siehe gallery-reorder.js.
        const reorder = window.CECGalleryReorder;
        const detachThumbs =
          reorder && typeof reorder.thumbnailIdsToDetach === "function"
            ? reorder.thumbnailIdsToDetach([...this.thumbById.keys()], idOrder)
            : [...this.thumbById.keys()].filter((id) => !showSet.has(id));
        detachThumbs.forEach((id) => {
          const tEl = this.thumbById.get(id);
          if (tEl && tEl.parentNode) tEl.parentNode.removeChild(tEl);
        });
        // Haupt-Slider: Zellen im gemeinsamen Parent neu anhängen (Parent =
        // .flickity-slider wenn aktiv, sonst .product-media-main-wrapper).
        // firstMain kann aus einem früheren Filter abgekoppelt sein → stabilen
        // Parent merken (_lastMainParent), sonst Fallback auf das Slideshow-Wrapper.
        const firstMain = this.mainById.get(idOrder[0]);
        const mainParent =
          firstMain && firstMain.parentNode
            ? firstMain.parentNode
            : this._lastMainParent || this.elements.slideshow;
        if (mainParent) {
          this._lastMainParent = mainParent;
          idOrder.forEach((id) => {
            const el = this.mainById.get(id);
            if (el) {
              mainParent.appendChild(el);
            }
          });
        }
        // Thumbnails neu anhängen.
        if (this.thumbsWrapper) {
          idOrder.forEach((id) => {
            const el = this.thumbById.get(id);
            if (el) {
              this.thumbsWrapper.appendChild(el);
            }
          });
        }
        // this.elements.mains/thumbnails sind statische NodeLists (im Constructor erfasst) —
        // nach dem Reorder auffrischen, damit setActiveMedia den korrekten neuen Index an
        // Flickity übergibt (sonst wird die falsche Zelle aktiviert).
        this.elements.mains = this.querySelectorAll(".product-media-main");
        this.elements.thumbnails = this.querySelectorAll(
          ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
        );
        // Flickity-Zellen neu einlesen; setActiveMedia übernimmt die Auswahl (select).
        if (this.flickity) {
          this.flickity.reloadCells();
          this.flickity.positionCells();
        }
        this.setActiveMedia(idOrder[0], false, true);
      }

      disconnectedCallback() {
        if (this.variantChangeUnsub) {
          this.variantChangeUnsub();
          this.variantChangeUnsub = null;
        }
      }
    },
  );
}

if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");

        this.zoomWidth = parseInt(this.getAttribute("data-zoom-width"), 10) || 150;
        this.zoomHeight = parseInt(this.getAttribute("data-zoom-height"), 10) || 150;

        this.lastEvent = null;
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.innerHTML += `<div class="media-zoom-zoom-area"></div>`;
        this.original = this.querySelector("img");
        this.zoomArea = this.querySelector(".media-zoom-zoom-area");

        this.original.addEventListener("mouseenter", this.onMouseEnter);
        this.original.addEventListener("mousemove", this.onMouseMove);
        this.original.addEventListener("mouseleave", this.onMouseLeave);

        if (this.mode === "lightbox_zoom_hover") {
          this.original.addEventListener("click", this.onClick);
        }

        this.zoomArea.style.width = `${this.zoomWidth}px`;
        this.zoomArea.style.height = `${this.zoomHeight}px`;

        this.preview = document.createElement("div");
        this.preview.classList.add(
          "media-zoom-preview-area",
          "color-background-1",
          "background-complementary",
        );
        this.preview.innerHTML = '<div class="spinner"></div>';
        document.body.appendChild(this.preview);
      }

      disconnectedCallback() {
        this.original.removeEventListener("mouseenter", this.onMouseEnter);
        this.original.removeEventListener("mousemove", this.onMouseMove);
        this.original.removeEventListener("mouseleave", this.onMouseLeave);
        document.body.removeChild(this.preview);
      }

      onMouseEnter() {
        this.zoomArea.style.display = "block";
        this.preview.style.display = "block";

        const previewSize = `${this.original.offsetWidth + 25}px`;
        this.preview.style.width = previewSize;
        this.preview.style.height = previewSize;

        // Load larger image on first mouse enter
        if (!this.previewImg) {
          this.previewImg = new Image();
          this.previewImg.src = this.getAttribute("data-zoom");
          this.previewImg.onload = () => {
            const zoomScale = this.original.offsetWidth / this.zoomWidth;

            this.previewImg.style.width = `${(this.original.offsetWidth + 25) * zoomScale}px`;
            this.previewImg.style.height = `${(this.original.offsetHeight + 25) * zoomScale}px`;

            this.preview.innerHTML = "";
            this.preview.appendChild(this.previewImg);
            if (this.lastEvent) {
              this.original.dispatchEvent(new MouseEvent("mousemove", this.lastEvent));
            }
          };
        } else {
          // Set the correct preview image always on mouse enter
          const zoomScale = this.original.offsetWidth / this.zoomWidth;
          this.previewImg.style.width = `${(this.original.offsetWidth + 25) * zoomScale}px`;
          this.previewImg.style.height = `${(this.original.offsetHeight + 25) * zoomScale}px`;
        }
      }

      onMouseMove(e) {
        this.lastEvent = e;
        const { left, top, width, height } = this.original.getBoundingClientRect();

        const x = e.pageX - (window.pageXOffset + left);
        const y = e.pageY - (window.pageYOffset + top);

        const zoomX = Math.min(Math.max(x - this.zoomWidth / 2, 0), width - this.zoomWidth);
        const zoomY = Math.min(Math.max(y - this.zoomHeight / 2, 0), height - this.zoomHeight);

        this.zoomArea.style.left = `${zoomX}px`;
        this.zoomArea.style.top = `${zoomY}px`;

        this.preview.style.position = "fixed";
        this.preview.style.left = `${left + width + 50}px`;
        this.preview.style.top = `${top - 14}px`;

        if (this.previewImg && this.previewImg.complete) {
          const previewX = (zoomX / width) * this.previewImg.width;
          const previewY = (zoomY / height) * this.previewImg.height;

          this.previewImg.style.transform = `translate(${-previewX}px, ${-previewY}px)`;
        }
      }

      onMouseLeave() {
        this.zoomArea.style.display = "none";
        this.preview.style.display = "none";
      }

      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}
